import LightbulbIcon from '@mui/icons-material/Lightbulb'
import { Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppParamsContext } from '../../context/AppParamsContext'
import { formatDateAndTime } from '../../helper/formatDate'
import TrafficViolationsServices from '../../services/TrafficViolationsServices'
import { TRAFFIC_URLS } from '../TrafficViolationsLookup/constants'
import HeaderMiniApp from '../../components/BasicComponent/HeaderMiniApp'
import Header from '../../components/BasicComponent/Header'

const resultTableContainerSx = {
  mb: 3,
  borderRadius: 2,
  borderColor: 'var(--v1-border-default)',
  backgroundColor: 'var(--v1-surface-card)',
  boxShadow: 'none',
  '& .MuiTableCell-root': {
    px: 2,
    py: 1.5,
    borderColor: 'var(--v1-border-default)',
    color: 'var(--v1-text-primary)',
    fontSize: '0.875rem',
    verticalAlign: 'top'
  }
}

const resultTableHeadCellSx = {
  width: '30%',
  backgroundColor: 'var(--v1-result-table-head-bg)',
  fontWeight: 500
}

const CRIMINAL_STATUS = {
  NO: 'Chưa xử phạt',
  YES: 'Đã xử phạt',
  NOT_CRIMINAL: 'Không vi phạm',
  API_ERROR: 'Lỗi tra cứu'
}

function NotificationBox() {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: 'var(--v1-result-notice-bg)',
        border: '1px solid',
        borderColor: 'var(--v1-result-notice-border)',
        boxShadow: 'none'
      }}>
      <LightbulbIcon
        sx={{
          mt: '2px',
          fontSize: 32,
          color: 'var(--v1-result-notice-icon)',
          flexShrink: 0
        }}
      />

      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ color: 'var(--v1-text-primary)' }}>
          Tự động tra cứu và gửi thông tin phạt nguội qua tin nhắn Zalo, SMS, Email.
        </Typography>

        <Box
          component="a"
          href={`${TRAFFIC_URLS.trafficFineWeb}/automated-traffic-fine-notification/vehicles`}
          target="_blank"
          rel="noreferrer"
          sx={{
            display: 'inline-block',
            color: 'var(--v1-brand-accent)',
            fontWeight: 700,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}>
          Đăng ký ngay
        </Box>
      </Box>
    </Paper>
  )
}

function ViolationTable({ violation, carType }) {
  const statusLabel = typeof violation?.crimeRecordStatus === 'string' ? violation.crimeRecordStatus.trim() : violation?.crimeRecordStatus
  const isSuccessStatus = statusLabel === CRIMINAL_STATUS.YES

  return (
    <TableContainer component={Paper} variant="outlined" sx={resultTableContainerSx}>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Biển số xe:
            </TableCell>
            <TableCell>{violation?.customerRecordPlatenumber || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Màu biển:
            </TableCell>
            <TableCell>{violation?.customerRecordPlateColorDesc || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Loại phương tiện:
            </TableCell>
            <TableCell>{carType || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Lỗi vi phạm
            </TableCell>
            <TableCell>{violation?.crimeRecordContent || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Thời gian vi phạm
            </TableCell>
            <TableCell>{violation?.crimeRecordTime ? formatDateAndTime(violation?.crimeRecordTime) : '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Địa điểm vi phạm
            </TableCell>
            <TableCell>{violation?.crimeRecordLocation || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Trạng thái
            </TableCell>
            <TableCell>
              {statusLabel ? (
                <Chip
                  label={statusLabel}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    backgroundColor: isSuccessStatus ? 'var(--v1-state-success-soft-bg)' : 'var(--v1-state-danger-soft-bg)',
                    color: isSuccessStatus ? 'var(--v1-state-success-text)' : 'var(--v1-state-danger-text)'
                  }}
                />
              ) : (
                '—'
              )}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Đơn vị phát hiện vi phạm
            </TableCell>
            <TableCell>{violation?.crimeRecordPIC || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Địa chỉ
            </TableCell>
            <TableCell>{violation?.crimeRecordAddressPIC || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Đơn vị tiếp nhận
            </TableCell>
            <TableCell>{violation?.crimeRecordAgency || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Địa chỉ
            </TableCell>
            <TableCell>{violation?.crimeRecordAddressAgency || '—'}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell component="th" scope="row" sx={resultTableHeadCellSx}>
              Ngày chỉnh sửa bổ sung
            </TableCell>
            <TableCell>{violation?.crimeRecordAmendmentDate || '—'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default function TrafficViolationsResult() {
  const location = useLocation()
  const { isEmbeddedView, isHeaderMiniApp } = useAppParamsContext()

  const query = useMemo(() => new URLSearchParams(location.search), [location.search])
  const licensePlate = query.get('lisencePlate')
  const carType = query.get('carType')
  const carTypeLabel = query.get('carTypeLabel')

  const [violations, setViolations] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    const fetchViolations = async () => {
      if (!licensePlate) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)

        const response = await TrafficViolationsServices.CustomerCriminalRecordUserGetCrimeRecords(
          {
            filter: {
              customerRecordPlatenumber: licensePlate,
              vehicleType: Number(carType)
            }
          },
          false
        )

        if (!isMounted) {
          return
        }

        if (response?.statusCode === 200) {
          setViolations(response?.data?.data || [])
        } else {
          setViolations([])
        }
      } catch {
        if (isMounted) {
          setViolations([])
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchViolations()

    return () => {
      isMounted = false
    }
  }, [licensePlate, carType])

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--v1-surface-page)' }}>
      {isEmbeddedView && !isHeaderMiniApp ? <></> : isHeaderMiniApp ? <HeaderMiniApp /> : <Header />}
      <Box
        sx={{
          width: '100%',
          maxWidth: isEmbeddedView ? '100%' : 800,
          margin: '0 auto',
          px: 2,
          pb: 2,
        }}>
        {!isEmbeddedView && (
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              textAlign: 'center',
              mt: 2,
              color: 'var(--v1-text-primary)'
            }}>
            Chi Tiết Vi Phạm
          </Typography>
        )}

        <Box sx={{ py: 1 }}>
          <Typography
            variant="subtitle1"
            component="h2"
            sx={{
              mt: 3,
              mb: 2,
              pb: 2,
              fontWeight: 700,
              color: 'var(--v1-text-primary)',
              borderBottom: '1px solid',
              borderColor: 'var(--v1-border-default)'
            }}>
            Thông tin vi phạm giao thông
          </Typography>

          {!isEmbeddedView && <NotificationBox />}

          {isLoading ? (
            <Typography variant="body1">Đang tải dữ liệu...</Typography>
          ) : violations.length > 0 ? (
            violations.map((violation, index) => (
              <ViolationTable
                key={`violation-${index}-${violation?.customerRecordPlatenumber || index}`}
                violation={violation}
                carType={carTypeLabel || carType}
              />
            ))
          ) : (
            <Typography variant="body1">Không tìm thấy dữ liệu vi phạm</Typography>
          )}
        </Box>
      </Box>
    </div>
  )
}
